import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getStats, getProfile, getWorkoutPlans } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import InstallCard from '../components/InstallCard';
import { useLanguage } from '../context/LanguageContext';
import { Loader2, Plus, Activity, Target, TrendingUp, Calendar, ChevronRight, User, Zap } from 'lucide-react';

const DashboardPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [recentPlans, setRecentPlans] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, profileData, plansData] = await Promise.all([
          getStats(),
          getProfile(),
          getWorkoutPlans()
        ]);
        setStats(statsData);
        setProfile(profileData);
        setRecentPlans(plansData.slice(0, 3));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#32CD32]" />
        </div>
      </DashboardLayout>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <DashboardLayout>
      <div data-testid="dashboard-page">
        {/* Welcome Section */}
        <div className="mb-8">
          <p className="text-xs font-mono tracking-[0.2em] text-[#32CD32] mb-2 uppercase">
            {t('welcomeBack')}
          </p>
          <h1 
            className="text-3xl md:text-5xl font-bold tracking-tight uppercase"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            {t('hello')}, <span className="text-[#32CD32]">{user?.name?.split(' ')[0]}</span>
          </h1>
        </div>

        {/* Alert if no profile */}
        {!profile && (
          <Card className="bg-[#32CD32]/10 border-[#32CD32]/30 mb-8">
            <CardContent className="p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <User className="w-8 h-8 text-[#32CD32] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-lg mb-1">{t('completeProfile')}</h3>
                  <p className="text-gray-400">
                    Preenche as tuas informações para receber planos de treino personalizados.
                  </p>
                </div>
              </div>
              <Link to="/profile">
                <Button className="btn-primary px-6 py-3 rounded-full whitespace-nowrap" data-testid="complete-profile-btn">
                  Completar Perfil
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid - Bento Style */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-4 md:gap-6 mb-8">
          {/* Main CTA Card */}
          <Card className="col-span-2 md:col-span-8 bg-gradient-to-br from-[#121212] to-[#1a1a1a] border-white/10 card-hover overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#32CD32]/5 rounded-full blur-3xl" />
            <CardContent className="p-6 sm:p-8 relative z-10">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div>
                  <Zap className="w-10 h-10 sm:w-12 sm:h-12 text-[#32CD32] mb-4" />
                  <h2 
                    className="text-2xl md:text-3xl font-bold tracking-tight uppercase mb-2"
                    style={{ fontFamily: 'Barlow Condensed' }}
                  >
                    {t('generateNewPlan')}
                  </h2>
                  <p className="text-sm sm:text-base text-gray-400 max-w-md">
                    {t('generateNewPlanDesc')}
                  </p>
                </div>
                <Link to="/generate" className="w-full md:w-auto">
                  <Button 
                    className="btn-primary w-full md:w-auto px-8 py-5 sm:py-6 rounded-full text-base sm:text-lg animate-pulse-glow"
                    disabled={!profile}
                    data-testid="generate-plan-btn"
                  >
                    <Plus className="mr-2 w-5 h-5" />
                    {t('createPlan')}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Total Plans */}
          <Card className="md:col-span-4 bg-[#121212] border-white/10 card-hover">
            <CardContent className="p-4 sm:p-6 flex flex-col justify-between h-full">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#32CD32]/10 flex items-center justify-center mb-3 sm:mb-4">
                <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-[#32CD32]" />
              </div>
              <div>
                <p className="text-[10px] sm:text-xs font-mono tracking-widest text-[#A1A1AA]/90 uppercase mb-1">{t('totalPlans')}</p>
                <p className="text-3xl sm:text-4xl font-bold text-[#32CD32]" data-testid="stats-total-plans">
                  {stats?.total_plans || 0}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Sessions Completed */}
          <Card className="md:col-span-4 bg-[#121212] border-white/10 card-hover">
            <CardContent className="p-4 sm:p-6 flex flex-col justify-between h-full">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#32CD32]/10 flex items-center justify-center mb-3 sm:mb-4">
                <Target className="w-5 h-5 sm:w-6 sm:h-6 text-[#32CD32]" />
              </div>
              <div>
                <p className="text-[10px] sm:text-xs font-mono tracking-widest text-[#A1A1AA]/90 uppercase mb-1">{t('completedSessions')}</p>
                <p className="text-3xl sm:text-4xl font-bold" data-testid="stats-total-sessions">
                  {stats?.total_sessions_completed || 0}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Average Rating */}
          <Card className="md:col-span-4 bg-[#121212] border-white/10 card-hover">
            <CardContent className="p-4 sm:p-6 flex flex-col justify-between h-full">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#32CD32]/10 flex items-center justify-center mb-3 sm:mb-4">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-[#32CD32]" />
              </div>
              <div>
                <p className="text-[10px] sm:text-xs font-mono tracking-widest text-[#A1A1AA]/90 uppercase mb-1">{t('averageRating')}</p>
                <p className="text-3xl sm:text-4xl font-bold" data-testid="stats-avg-rating">
                  {stats?.average_rating || '-'}<span className="text-lg text-gray-500">/5</span>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Progress Entries */}
          <Card className="md:col-span-4 bg-[#121212] border-white/10 card-hover">
            <CardContent className="p-4 sm:p-6 flex flex-col justify-between h-full">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#32CD32]/10 flex items-center justify-center mb-3 sm:mb-4">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-[#32CD32]" />
              </div>
              <div>
                <p className="text-[10px] sm:text-xs font-mono tracking-widest text-[#A1A1AA]/90 uppercase mb-1">{t('progressRecords')}</p>
                <p className="text-3xl sm:text-4xl font-bold" data-testid="stats-progressions">
                  {stats?.total_progressions || 0}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Install as mobile app */}
        <div className="mb-6">
          <InstallCard />
        </div>

        {/* Recent Plans */}
        <Card className="bg-[#121212] border-white/10">
          <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle 
                className="text-xl uppercase tracking-tight"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                {t('recentPlans')}
              </CardTitle>
              <CardDescription>{t('recentPlansDesc')}</CardDescription>
            </div>
            {recentPlans.length > 0 && (
              <Link to="/history">
                <Button variant="ghost" className="text-[#32CD32] hover:text-[#32CD32]/80" data-testid="view-all-plans-btn">
                  {t('seeAll')}
                  <ChevronRight className="ml-1 w-4 h-4" />
                </Button>
              </Link>
            )}
          </CardHeader>
          <CardContent>
            {recentPlans.length === 0 ? (
              <div className="text-center py-12">
                <Activity className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">{t('noPlansYet')}</p>
                <Link to="/generate">
                  <Button className="btn-primary px-6 py-3 rounded-full" disabled={!profile} data-testid="create-first-plan-btn">
                    Criar Primeiro Plano
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {recentPlans.map((plan) => (
                  <Link 
                    key={plan.id} 
                    to={`/history/${plan.id}`}
                    className="block"
                    data-testid={`plan-card-${plan.id}`}
                  >
                    <div className="p-4 rounded-xl bg-black/30 border border-white/5 hover:border-[#32CD32]/30 transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium mb-1">
                            {t('planWeeks').replace('{weeks}', plan.duration_weeks)}
                            {plan.focus_area && <span className="text-[#32CD32]"> • {plan.focus_area}</span>}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatDate(plan.created_at)}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
